<template>
  <TransitionRoot :show="visible" as="template">
    <Dialog as="div" class="relative z-50" @close="handleClose">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-50" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-2xl transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all">
              <!-- 头部 -->
              <div class="px-6 py-4 border-b border-gray-200">
                <div class="flex items-center justify-between">
                  <DialogTitle class="text-lg font-semibold text-gray-900">AI智能生成品牌独立站</DialogTitle>
                  <button @click="handleClose" class="text-gray-400 hover:text-gray-600 transition-colors">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <!-- 步骤指示器 -->
              <div class="px-6 py-4 bg-gray-50">
                <div class="flex items-center justify-center max-w-3xl mx-auto">
                  <div v-for="(step, index) in steps" :key="index" class="flex items-center">
                    <div class="flex flex-col items-center">
                      <div :class="[
                        'w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                        currentStep >= index ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-200 text-gray-500'
                      ]">
                        {{ currentStep > index ? '✓' : index + 1 }}
                      </div>
                      <div class="mt-2 text-xs font-medium whitespace-nowrap" :class="currentStep >= index ? 'text-gray-900' : 'text-gray-500'">
                        {{ step.title }}
                      </div>
                    </div>
                    <div v-if="index < steps.length - 1" 
                         :class="['w-16 h-0.5 mx-4 mt-0 mb-6 transition-colors', currentStep > index ? 'bg-blue-600' : 'bg-gray-300']">
                    </div>
                  </div>
                </div>
              </div>

              <!-- 内容区域 -->
              <div class="px-6 py-6" style="min-height: 350px;" @click="closeAllDropdowns">
                <!-- Step 1: 基础信息 -->
                <div v-if="currentStep === 0" class="space-y-6">
                  <!-- 行业类型 -->
                  <div class="relative" ref="industrySelectRef" @click.stop>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      行业类型 <span class="text-red-500">*</span>
                    </label>
                    <!-- 触发器 -->
                    <div
                      @click.stop="toggleIndustryDropdown"
                      class="relative w-full h-10 cursor-pointer bg-white border border-gray-300 rounded-lg px-3 text-left shadow-sm hover:border-blue-500 hover:ring-1 hover:ring-blue-500 transition-colors flex items-center"
                      :class="{ 'border-blue-500 ring-1 ring-blue-500': isIndustryOpen }"
                    >
                      <span class="flex-1 truncate text-sm text-gray-900">
                        {{ industryOptions.find(opt => opt.value === formData.industry)?.label }}
                      </span>
                      <span class="pointer-events-none flex items-center pl-2">
                        <svg 
                          class="h-5 w-5 text-gray-400 transition-transform duration-200"
                          :class="{ 'rotate-180': isIndustryOpen }"
                          xmlns="http://www.w3.org/2000/svg" 
                          viewBox="0 0 20 20" 
                          fill="currentColor"
                        >
                          <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                        </svg>
                      </span>
                    </div>
                    
                    <!-- 下拉列表 -->
                    <transition
                      enter-active-class="transition duration-100 ease-out"
                      enter-from-class="transform scale-95 opacity-0"
                      enter-to-class="transform scale-100 opacity-100"
                      leave-active-class="transition duration-75 ease-in"
                      leave-from-class="transform scale-100 opacity-100"
                      leave-to-class="transform scale-95 opacity-0"
                    >
                      <div
                        v-show="isIndustryOpen"
                        class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none"
                      >
                        <div
                          v-for="option in industryOptions"
                          :key="option.value"
                          @click.stop="selectIndustry(option.value)"
                          class="group relative cursor-pointer select-none py-3 pl-3 pr-9 text-gray-900 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                          :class="{ 'bg-blue-100 text-blue-900': formData.industry === option.value }"
                        >
                          <span class="block truncate text-sm font-medium">
                            {{ option.label }}
                          </span>
                          <span 
                            v-if="formData.industry === option.value"
                            class="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600"
                          >
                            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </transition>
                  </div>

                  <!-- 站点语言 -->
                  <div class="relative" ref="languageSelectRef" @click.stop>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      站点语言 <span class="text-red-500">*</span>
                    </label>
                    <!-- 触发器 -->
                    <div
                      @click.stop="toggleLanguageDropdown"
                      class="relative w-full h-10 cursor-pointer bg-white border border-gray-300 rounded-lg px-3 text-left shadow-sm hover:border-blue-500 hover:ring-1 hover:ring-1 hover:ring-blue-500 transition-colors flex items-center"
                      :class="{ 'border-blue-500 ring-1 ring-blue-500': isLanguageOpen }"
                    >
                      <span class="flex-1 truncate text-sm text-gray-900">
                        {{ languageOptions.find(opt => opt.value === formData.language)?.label }}
                      </span>
                      <span class="pointer-events-none flex items-center pl-2">
                        <svg 
                          class="h-5 w-5 text-gray-400 transition-transform duration-200"
                          :class="{ 'rotate-180': isLanguageOpen }"
                          xmlns="http://www.w3.org/2000/svg" 
                          viewBox="0 0 20 20" 
                          fill="currentColor"
                        >
                          <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                        </svg>
                      </span>
                    </div>
                    
                    <!-- 下拉列表 -->
                    <transition
                      enter-active-class="transition duration-100 ease-out"
                      enter-from-class="transform scale-95 opacity-0"
                      enter-to-class="transform scale-100 opacity-100"
                      leave-active-class="transition duration-75 ease-in"
                      leave-from-class="transform scale-100 opacity-100"
                      leave-to-class="transform scale-95 opacity-0"
                    >
                      <div
                        v-show="isLanguageOpen"
                        class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none"
                      >
                        <div
                          v-for="option in languageOptions"
                          :key="option.value"
                          @click.stop="selectLanguage(option.value)"
                          class="group relative cursor-pointer select-none py-3 pl-3 pr-9 text-gray-900 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                          :class="{ 'bg-blue-100 text-blue-900': formData.language === option.value }"
                        >
                          <span class="block truncate text-sm font-medium">
                            {{ option.label }}
                          </span>
                          <span 
                            v-if="formData.language === option.value"
                            class="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600"
                          >
                            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </transition>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">品牌名称</label>
                    <input v-model="formData.brand_name" type="text" placeholder="留空将使用行业名称"
                           class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">品牌标语</label>
                    <input v-model="formData.brand_slogan" type="text" placeholder="AI将根据行业生成"
                           class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
                  </div>
                </div>

                <!-- Step 2: 功能配置 -->
                <div v-if="currentStep === 1" class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-3">页面栏目</label>
                    <div class="grid grid-cols-2 gap-3">
                      <label class="flex items-start space-x-2 cursor-pointer p-2.5 rounded-lg hover:bg-gray-50 border border-gray-200">
                        <input v-model="formData.need_products" type="checkbox" class="mt-1 h-4 w-4 text-blue-600 rounded" />
                        <div class="flex-1">
                          <div class="text-sm font-medium text-gray-900">产品展示</div>
                          <div class="text-xs text-gray-500">展示产品列表和详情</div>
                        </div>
                      </label>

                      <label class="flex items-start space-x-2 cursor-pointer p-2.5 rounded-lg hover:bg-gray-50 border border-gray-200">
                        <input v-model="formData.need_about" type="checkbox" class="mt-1 h-4 w-4 text-blue-600 rounded" />
                        <div class="flex-1">
                          <div class="text-sm font-medium text-gray-900">关于我们</div>
                          <div class="text-xs text-gray-500">公司介绍和发展历程</div>
                        </div>
                      </label>

                      <label class="flex items-start space-x-2 cursor-pointer p-2.5 rounded-lg hover:bg-gray-50 border border-gray-200">
                        <input v-model="formData.need_team" type="checkbox" class="mt-1 h-4 w-4 text-blue-600 rounded" />
                        <div class="flex-1">
                          <div class="text-sm font-medium text-gray-900">团队介绍</div>
                          <div class="text-xs text-gray-500">展示核心团队成员</div>
                        </div>
                      </label>

                      <label class="flex items-start space-x-2 cursor-pointer p-2.5 rounded-lg hover:bg-gray-50 border border-gray-200">
                        <input v-model="formData.need_cases" type="checkbox" class="mt-1 h-4 w-4 text-blue-600 rounded" />
                        <div class="flex-1">
                          <div class="text-sm font-medium text-gray-900">客户案例</div>
                          <div class="text-xs text-gray-500">成功案例和客户评价</div>
                        </div>
                      </label>

                      <label class="flex items-start space-x-2 cursor-pointer p-2.5 rounded-lg hover:bg-gray-50 border border-gray-200">
                        <input v-model="formData.need_partners" type="checkbox" class="mt-1 h-4 w-4 text-blue-600 rounded" />
                        <div class="flex-1">
                          <div class="text-sm font-medium text-gray-900">合作伙伴</div>
                          <div class="text-xs text-gray-500">合作伙伴logo展示</div>
                        </div>
                      </label>

                      <label class="flex items-start space-x-2 cursor-pointer p-2.5 rounded-lg hover:bg-gray-50 border border-gray-200">
                        <input v-model="formData.need_contact_form" type="checkbox" class="mt-1 h-4 w-4 text-blue-600 rounded" />
                        <div class="flex-1">
                          <div class="text-sm font-medium text-gray-900">联系表单</div>
                          <div class="text-xs text-gray-500">访客可以通过表单提交咨询</div>
                        </div>
                      </label>

                      <label class="flex items-start space-x-2 cursor-pointer p-2.5 rounded-lg hover:bg-gray-50 border border-gray-200">
                        <input v-model="formData.need_faq" type="checkbox" class="mt-1 h-4 w-4 text-blue-600 rounded" />
                        <div class="flex-1">
                          <div class="text-sm font-medium text-gray-900">FAQ常见问题</div>
                          <div class="text-xs text-gray-500">展示常见问题解答</div>
                        </div>
                      </label>

                      <label class="flex items-start space-x-2 cursor-pointer p-2.5 rounded-lg hover:bg-gray-50 border border-gray-200">
                        <input v-model="formData.need_video" type="checkbox" class="mt-1 h-4 w-4 text-blue-600 rounded" />
                        <div class="flex-1">
                          <div class="text-sm font-medium text-gray-900">视频展示区</div>
                          <div class="text-xs text-gray-500">可嵌入YouTube或Vimeo视频</div>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  <div v-if="formData.need_products">
                    <label class="block text-sm font-medium text-gray-700 mb-3">产品设置</label>
                    <label class="flex items-start space-x-2 cursor-pointer p-2.5 rounded-lg hover:bg-gray-50 border border-gray-200">
                      <input v-model="formData.need_amazon_link" type="checkbox" class="mt-1 h-4 w-4 text-blue-600 rounded" />
                      <div class="flex-1">
                        <div class="text-sm font-medium text-gray-900">亚马逊购买链接</div>
                        <div class="text-xs text-gray-500">产品卡片包含亚马逊购买按钮</div>
                      </div>
                    </label>
                  </div>
                </div>

                <!-- Step 3: 设计定制 -->
                <div v-if="currentStep === 2" class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-3">配色方案</label>
                    
                    <!-- 自定义配色 -->
                    <div class="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label class="block text-xs font-medium text-gray-600 mb-1">主色调</label>
                        <div class="flex items-center space-x-2">
                          <input v-model="formData.primary_color" type="color"
                                 class="h-10 w-16 border border-gray-300 rounded-lg cursor-pointer" />
                          <span class="text-gray-700 font-mono text-xs">{{ formData.primary_color }}</span>
                        </div>
                      </div>
                      
                      <div>
                        <label class="block text-xs font-medium text-gray-600 mb-1">辅助色</label>
                        <div class="flex items-center space-x-2">
                          <input v-model="formData.secondary_color" type="color"
                                 class="h-10 w-16 border border-gray-300 rounded-lg cursor-pointer" />
                          <span class="text-gray-700 font-mono text-xs">{{ formData.secondary_color }}</span>
                        </div>
                      </div>
                    </div>
                    
                    <!-- 预设配色 -->
                    <div>
                      <label class="block text-xs font-medium text-gray-600 mb-2">预设配色</label>
                      <div class="h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
                        <div class="grid grid-cols-4 gap-2">
                          <button
                            v-for="preset in colorPresets"
                            :key="preset.name"
                            @click="applyColorPreset(preset)"
                            class="flex flex-col items-center p-2 border border-gray-200 rounded-lg hover:border-blue-500 transition-colors"
                            :title="preset.name"
                          >
                            <div class="flex space-x-1 mb-1">
                              <div class="w-8 h-8 rounded" :style="{ backgroundColor: preset.primary }"></div>
                              <div class="w-8 h-8 rounded" :style="{ backgroundColor: preset.secondary }"></div>
                            </div>
                            <span class="text-xs text-gray-600">{{ preset.name }}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">其他要求</label>
                    <textarea 
                      v-model="formData.custom_requirements" 
                      placeholder="例如：添加视频背景、使用圆角设计、强调环保理念..." 
                      rows="3"
                      class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
                    ></textarea>
                    <p class="text-xs text-gray-500 mt-1">AI将根据您的特殊要求调整网站设计</p>
                  </div>
                </div>

                <!-- Step 4: 生成中/预览 -->
                <div v-if="currentStep === 3" class="flex flex-col items-center justify-center py-8">
                  <div v-if="generating" class="text-center">
                    <div class="inline-block animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
                    <p class="text-lg font-medium text-gray-700 mb-2">AI正在为您生成独特的品牌独立站...</p>
                    <p class="text-sm text-gray-500 mb-2">预计需要 2-5 分钟</p>
                    <p class="text-xs text-gray-400 mb-4">正在生成详细的HTML代码，请耐心等待...</p>
                    <div class="w-80 bg-gray-200 rounded-full h-2.5">
                      <div class="bg-blue-600 h-2.5 rounded-full transition-all duration-500" :style="{ width: progress + '%' }"></div>
                    </div>
                    <p class="text-xs text-gray-400 mt-2">{{ Math.round(progress) }}%</p>
                  </div>

                  <div v-else-if="generatedSite" class="w-full space-y-4">
                    <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div class="flex items-center">
                        <CheckCircleIcon class="w-5 h-5 text-green-600 mr-2" />
                        <span class="text-green-800 font-medium">生成成功！</span>
                      </div>
                      <p class="text-sm text-green-700 mt-1">您的品牌独立站已生成完成，可以预览或直接编辑</p>
                    </div>

                    <div class="border border-gray-200 rounded-lg overflow-hidden">
                      <div class="bg-gray-50 px-4 py-3 flex justify-between items-center">
                        <h4 class="font-medium text-gray-700">站点预览</h4>
                        <button @click="openPreview" class="text-blue-600 hover:text-blue-700 text-sm flex items-center">
                          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          在新标签页打开
                        </button>
                      </div>
                      <div class="bg-white">
                        <iframe v-if="previewHtml" :srcdoc="previewHtml" class="w-full h-96 border-0"></iframe>
                      </div>
                    </div>

                    <div class="flex justify-center gap-3 pt-4">
                      <button @click="regenerate"
                              class="px-6 py-2.5 text-sm font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center">
                        <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        重新生成
                      </button>
                      <button @click="saveAndEdit"
                              class="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center">
                        <CheckCircleIcon class="w-4 h-4 mr-1.5" />
                        确认并编辑
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 底部按钮 -->
              <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
                <button
                  v-if="currentStep > 0 && currentStep < 3"
                  @click="prevStep"
                  class="px-6 py-2.5 text-sm font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  上一步
                </button>
                <div v-else></div>

                <div class="flex gap-2">
                  <button @click="handleClose"
                          class="px-6 py-2.5 text-sm font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">
                    取消
                  </button>
                  <button
                    v-if="currentStep < 2"
                    @click="nextStep"
                    class="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    下一步
                  </button>
                  <button
                    v-if="currentStep === 2"
                    @click="generateWithAI"
                    :disabled="generating"
                    class="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {{ generating ? '生成中...' : '开始生成' }}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useToast } from 'vue-toastification'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionRoot,
  TransitionChild,
} from '@headlessui/vue'
import { CheckCircleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import brandSiteService from '@/services/brandSiteService'
import productService from '@/services/productService'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'generated'])

const toast = useToast()

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const steps = [
  { title: '基础信息' },
  { title: '功能配置' },
  { title: '设计定制' },
  { title: '生成预览' }
]

const currentStep = ref(0)
const generating = ref(false)
const progress = ref(0)
const isSearching = ref(false)
const productSearchQuery = ref('')
const productSearchResults = ref([])
const generatedSite = ref(null)
const previewHtml = ref('')

// 下拉框状态和引用
const isIndustryOpen = ref(false)
const isLanguageOpen = ref(false)
const industrySelectRef = ref(null)
const languageSelectRef = ref(null)

// 行业选项
const industryOptions = [
  { value: 'technology', label: '科技电子' },
  { value: 'beauty', label: '美妆护肤' },
  { value: 'home', label: '家居生活' },
  { value: 'fashion', label: '时尚服饰' },
  { value: 'food', label: '食品饮料' },
  { value: 'sports', label: '运动健身' },
  { value: 'toys', label: '玩具游戏' },
  { value: 'automotive', label: '汽车配件' },
  { value: 'health', label: '健康保健' },
  { value: 'jewelry', label: '珠宝配饰' }
]

// 语言选项
const languageOptions = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'ja', label: '日本語' },
  { value: 'zh', label: '中文' }
]

// 初始表单数据
const getInitialFormData = () => ({
  industry: 'technology',
  language: 'en',
  primary_color: '#3B82F6',
  secondary_color: '#10B981',
  need_products: true,
  need_about: true,
  need_team: false,
  need_cases: false,
  need_partners: false,
  need_contact_form: true,
  need_amazon_link: true,
  need_faq: false,
  need_video: false,
  brand_name: '',
  brand_slogan: '',
  custom_requirements: ''
})

// 表单数据
const formData = ref(getInitialFormData())

// 行业推荐配色
const industryColors = {
  technology: { primary: '#3B82F6', secondary: '#10B981' },
  beauty: { primary: '#EC4899', secondary: '#F59E0B' },
  home: { primary: '#F59E0B', secondary: '#10B981' },
  fashion: { primary: '#8B5CF6', secondary: '#EC4899' },
  food: { primary: '#F59E0B', secondary: '#EF4444' },
  sports: { primary: '#EF4444', secondary: '#3B82F6' },
  toys: { primary: '#EC4899', secondary: '#F59E0B' },
  automotive: { primary: '#1F2937', secondary: '#EF4444' },
  health: { primary: '#10B981', secondary: '#3B82F6' },
  jewelry: { primary: '#8B5CF6', secondary: '#D4AF37' }
}

// 预设配色方案
const colorPresets = [
  { name: '经典黑白', primary: '#000000', secondary: '#FFFFFF' },
  { name: '极简灰', primary: '#6B7280', secondary: '#D1D5DB' },
  { name: '高级灰', primary: '#374151', secondary: '#9CA3AF' },
  { name: '简约黑', primary: '#111827', secondary: '#4B5563' },
  { name: '科技蓝', primary: '#3B82F6', secondary: '#10B981' },
  { name: '活力橙', primary: '#F59E0B', secondary: '#EF4444' },
  { name: '优雅紫', primary: '#8B5CF6', secondary: '#EC4899' },
  { name: '清新绿', primary: '#10B981', secondary: '#3B82F6' },
  { name: '商务蓝', primary: '#1E40AF', secondary: '#0891B2' },
  { name: '温暖红', primary: '#EF4444', secondary: '#F59E0B' },
  { name: '经典黑金', primary: '#1F2937', secondary: '#D4AF37' },
  { name: '粉嫩系', primary: '#EC4899', secondary: '#F472B6' },
  { name: '天空蓝', primary: '#0EA5E9', secondary: '#38BDF8' },
  { name: '薄荷绿', primary: '#14B8A6', secondary: '#2DD4BF' },
  { name: '深海蓝', primary: '#1E3A8A', secondary: '#3B82F6' },
  { name: '玫瑰金', primary: '#BE185D', secondary: '#F472B6' },
  { name: '森林绿', primary: '#047857', secondary: '#10B981' },
  { name: '酒红色', primary: '#9F1239', secondary: '#E11D48' },
  { name: '琥珀色', primary: '#D97706', secondary: '#FBBF24' },
  { name: '靛蓝色', primary: '#4338CA', secondary: '#6366F1' }
]

// 已选产品（用于显示）
const selectedProducts = computed(() => {
  return productSearchResults.value.filter(p => formData.value.product_ids.includes(p.id))
})

// 搜索产品
let searchTimer = null
const searchProducts = () => {
  const query = productSearchQuery.value.trim()
  if (!query) {
    productSearchResults.value = []
    return
  }
  
  clearTimeout(searchTimer)
  searchTimer = setTimeout(async () => {
    isSearching.value = true
    try {
      const response = await productService.getProductSuggestions(query)
      productSearchResults.value = response.data || []
    } catch (error) {
      console.error('搜索产品失败:', error)
    } finally {
      isSearching.value = false
    }
  }, 300)
}

// 切换产品选择
const toggleProduct = (product) => {
  const index = formData.value.product_ids.indexOf(product.id)
  if (index > -1) {
    formData.value.product_ids.splice(index, 1)
  } else {
    formData.value.product_ids.push(product.id)
  }
}

// 移除产品
const removeProduct = (productId) => {
  const index = formData.value.product_ids.indexOf(productId)
  if (index > -1) {
    formData.value.product_ids.splice(index, 1)
  }
}

// 下拉框方法
const toggleIndustryDropdown = () => {
  isIndustryOpen.value = !isIndustryOpen.value
  if (isLanguageOpen.value) isLanguageOpen.value = false
}

const toggleLanguageDropdown = () => {
  isLanguageOpen.value = !isLanguageOpen.value
  if (isIndustryOpen.value) isIndustryOpen.value = false
}

const selectIndustry = (value) => {
  formData.value.industry = value
  isIndustryOpen.value = false
}

const selectLanguage = (value) => {
  formData.value.language = value
  isLanguageOpen.value = false
}

// 关闭所有下拉框
const closeAllDropdowns = () => {
  isIndustryOpen.value = false
  isLanguageOpen.value = false
}

// 点击外部关闭下拉框
const handleClickOutside = (event) => {
  if (industrySelectRef.value && !industrySelectRef.value.contains(event.target)) {
    isIndustryOpen.value = false
  }
  if (languageSelectRef.value && !languageSelectRef.value.contains(event.target)) {
    isLanguageOpen.value = false
  }
}

// 应用预设配色
const applyColorPreset = (preset) => {
  formData.value.primary_color = preset.primary
  formData.value.secondary_color = preset.secondary
  toast.success(`已应用${preset.name}配色`)
}

// 应用行业推荐配色
const applyIndustryColors = () => {
  const colors = industryColors[formData.value.industry]
  if (colors) {
    formData.value.primary_color = colors.primary
    formData.value.secondary_color = colors.secondary
    toast.success('已应用行业推荐配色')
  }
}

// 步骤导航
const nextStep = () => {
  if (currentStep.value < 3) {
    currentStep.value++
  }
}

const prevStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

// AI生成
const generateWithAI = async () => {
  generating.value = true
  progress.value = 0
  currentStep.value = 3
  
  // 模拟进度（适应2-5分钟的生成时间）
  const progressInterval = setInterval(() => {
    if (progress.value < 80) {
      // 前80%慢速增长（约3-4分钟到80%）
      progress.value += Math.random() * 1.5 + 0.5
    } else if (progress.value < 90) {
      // 80-90%更慢
      progress.value += Math.random() * 0.5 + 0.2
    } else if (progress.value < 95) {
      // 90-95%极慢（等待真实结果）
      progress.value += Math.random() * 0.2
    }
  }, 2000)  // 每2秒更新一次
  
  try {
    const response = await brandSiteService.generateWithAI(formData.value)
    
    if (response.success) {
      generatedSite.value = response.site
      previewHtml.value = response.site.html_content
      progress.value = 100
      toast.success('AI生成成功！')
    } else {
      throw new Error(response.error || 'AI生成失败')
    }
  } catch (error) {
    console.error('AI生成失败:', error)
    toast.error(error.message || 'AI生成失败，请重试')
    currentStep.value = 2 // 回到配置页
  } finally {
    clearInterval(progressInterval)
    generating.value = false
  }
}

// 重新生成
const regenerate = () => {
  generatedSite.value = null
  previewHtml.value = ''
  currentStep.value = 2
}

// 在新标签页打开预览
const openPreview = () => {
  if (generatedSite.value && generatedSite.value.full_url) {
    window.open(generatedSite.value.full_url, '_blank')
  }
}

// 保存并编辑
const saveAndEdit = () => {
  if (generatedSite.value) {
    emit('generated', generatedSite.value)
    handleClose()
  }
}

// 关闭对话框
const handleClose = () => {
  // 重置所有状态
  currentStep.value = 0
  generating.value = false
  progress.value = 0
  generatedSite.value = null
  previewHtml.value = ''
  productSearchQuery.value = ''
  productSearchResults.value = []
  
  // 关闭下拉框
  isIndustryOpen.value = false
  isLanguageOpen.value = false
  
  // 重置表单数据为初始状态
  formData.value = getInitialFormData()
  
  visible.value = false
}

// 生命周期钩子
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* 自定义样式 */
</style>
